import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService, Notification } from '../../services/notificationService';
import { Button } from '../../components/UI/Button';
import { useToast } from '../../components/UI/ToastProvider';
import { NOTIFICATIONS_INBOX_UPDATED } from '../../hooks/useSignalR';
import { navigateNotificationActionUrl } from '../../utils/notificationActionUrl';

export const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);
  const [deletingNotifications, setDeletingNotifications] = useState<Set<string>>(new Set());
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { addToast } = useToast();

  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        // Testar conectividade primeiro
        const isConnected = await notificationService.testConnection();
        
        if (isConnected) {
          await loadNotifications();
        } else {
          setHasError(true);
          setErrorMessage('Não foi possível conectar com o servidor. Verifique sua conexão com a internet.');
        }
      } catch (error) {
        console.error('Erro na inicialização:', error);
        setHasError(true);
        setErrorMessage('Erro ao inicializar notificações.');
      }
    };

    initializeNotifications();

    const onInboxUpdated = () => {
      void loadNotifications();
    };
    window.addEventListener(NOTIFICATIONS_INBOX_UPDATED, onInboxUpdated);
    return () => {
      window.removeEventListener(NOTIFICATIONS_INBOX_UPDATED, onInboxUpdated);
    };
  }, []);

  const loadNotifications = async () => {
    try {
      setIsLoading(true);
      setHasError(false);
      setErrorMessage('');
      const allNotifications = await notificationService.getNotifications();
      setNotifications(allNotifications);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      setHasError(true);
      setErrorMessage('Não foi possível carregar as notificações. Verifique sua conexão com a internet e tente novamente.');
      addToast('Erro ao carregar notificações', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setIsMarkingAllAsRead(true);
      await notificationService.markAllAsRead();
      await loadNotifications(); // Recarregar para atualizar o estado
      addToast('Todas as notificações foram marcadas como lidas', 'success');
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
      addToast('Não foi possível marcar as notificações como lidas. Verifique sua conexão e tente novamente.', 'error');
    } finally {
      setIsMarkingAllAsRead(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      // Atualizar o estado local
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true }
            : notification
        )
      );
      
      addToast('Notificação marcada como lida', 'success');
    } catch (error) {
      console.error('Erro detalhado ao marcar notificação como lida:', error);
      console.error('NotificationId:', notificationId);
      console.error('Error object:', error);
      
      // Verificar se é um erro de rede
      if (error instanceof Error) {
        if (error.message.includes('Network Error') || error.message.includes('fetch')) {
          addToast('Erro de conexão. Verifique sua internet e tente novamente.', 'error');
        } else {
          addToast('Não foi possível marcar a notificação como lida. Tente novamente.', 'error');
        }
      } else {
        addToast('Erro inesperado ao marcar notificação como lida.', 'error');
      }
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      setDeletingNotifications(prev => new Set(prev).add(notificationId));
      await notificationService.deleteNotification(notificationId);
      // Remover do estado local
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      addToast('Notificação excluída com sucesso', 'success');
    } catch (error) {
      console.error('Erro ao excluir notificação:', error);
      addToast('Não foi possível excluir a notificação. Tente novamente.', 'error');
    } finally {
      setDeletingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const formatDate = (date: string) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInMinutes = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Agora mesmo';
    if (diffInMinutes < 60) return `${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''} atrás`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hora${Math.floor(diffInMinutes / 60) > 1 ? 's' : ''} atrás`;
    return notificationDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getNotificationIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'success':
        return 'text-green-500';
      case 'warning':
        return 'text-yellow-500';
      case 'error':
        return 'text-red-500';
      case 'info':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  const getNotificationIconSvg = (type: string) => {
    switch (type.toLowerCase()) {
      case 'success':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'error':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="flex-1">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notificações</h1>
          {unreadCount > 0 && (
            <p className="text-sm text-gray-600 mt-1">
              {unreadCount} notificaç{unreadCount > 1 ? 'ões' : 'ão'} não lida{unreadCount > 1 ? 's' : ''}
            </p>
          )}
        </div>
        {unreadCount > 0 && (
          <Button 
            onClick={handleMarkAllAsRead}
            disabled={isMarkingAllAsRead}
          >
            {isMarkingAllAsRead ? (
              <>
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Marcando...
              </>
            ) : (
              'Marcar todas como lidas'
            )}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : hasError ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <div className="mb-4">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erro de Conexão</h3>
          <p className="text-gray-500 mb-4 max-w-md">{errorMessage}</p>
          <Button onClick={loadNotifications} className="bg-blue-600 hover:bg-blue-700">
            Tentar Novamente
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm">
          {notifications.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => {
                    if (notification.actionUrl)
                    {
                      navigateNotificationActionUrl(notification.actionUrl, navigate);
                    }
                  }}
                  className={`p-4 hover:bg-gray-50 transition-colors duration-150 ${
                    !notification.read ? 'bg-blue-50' : ''
                  } ${notification.actionUrl ? 'cursor-pointer' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1">
                      <div className={`flex-shrink-0 ${getNotificationIcon(notification.type)}`}>
                        {getNotificationIconSvg(notification.type)}
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                          {!notification.read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notification.id);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Marcar como lida
                            </button>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-gray-500">{notification.message}</p>
                        <p className="mt-1 text-xs text-gray-400">{formatDate(notification.createdAt)}</p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNotification(notification.id);
                      }}
                      disabled={deletingNotifications.has(notification.id)}
                      className="ml-4 text-gray-400 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Excluir notificação"
                    >
                      {deletingNotifications.has(notification.id) ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma notificação</h3>
              <p className="text-gray-500">Você está em dia com suas notificações!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 